import type { OrderStatus } from '@cracked-mug/shared';
import { ORDER_STATUS_LABELS } from '@cracked-mug/shared';
import { cn } from '../../lib/cn';

/**
 * Color-coded pill for an order's status. Walks the pipeline from neutral
 * (waiting) → gold (in progress) → green (ready/done) → red (cancelled), so
 * an admin can read the order log at a glance instead of every row sharing
 * one gray pill. All pairings clear WCAG AA on the light surface. Pure
 * presentation — no data or behavior.
 */
const STATUS_CLASSES: Record<OrderStatus, string> = {
  pending: 'bg-wca-black/[0.05] text-wca-black/65',
  accepted: 'bg-wca-gold/15 text-[#7a5f15]',
  preparing: 'bg-wca-gold/20 text-[#7a5f15]',
  ready: 'bg-green-100 text-green-800',
  completed: 'bg-green-600/10 text-green-800',
  cancelled: 'bg-wca-red-light text-wca-red-dark',
};

export interface StatusBadgeProps {
  status: OrderStatus;
  className?: string;
}

/** Renders the labeled, color-coded status pill for an order. */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider',
        STATUS_CLASSES[status],
        className,
      )}
    >
      {ORDER_STATUS_LABELS[status]}
    </span>
  );
}
